import type { z } from 'zod';
import { skipToken, useQuery } from '@tanstack/react-query';
import type { ApiResponse } from '@/types/api-types';
import type { QueryOptions } from '@/types/tanstack-query-options';
import type { paths } from '~/generated/basic/api-schema';
import { genderCodeSchema } from '@/consts/backend-i18n-derived';
import { zodRemoveNullable } from '@/lib/zod-helper';
import { basicApi } from '@/services';
import { userDetailResSchema } from '~/generated/basic/zod-schema';

const PATH_VAR = '{userId}';
const API_PATH = `/v1/user/queryUser/${PATH_VAR}` as const;
const QUERY_KEY = '/v1/user/queryUser' as const;

export const QUERY_USER_DETAIL_KEY = QUERY_KEY;

type APIResponse = ApiResponse<paths, typeof API_PATH>;

const apiResponseDataSchema = zodRemoveNullable(userDetailResSchema, {
  excludeMask: {
    lastLoginDatetime: true,
    practitionerTypeCode: true,
    practitionerTypeName: true,
    userMobile: true,
    birthDate: true,
    gender: true,
    userEmp: true,
    empNoLockVersion: true,
  },
}).extend({
  genderI18n: genderCodeSchema.nullable(),
});

export type QueryUserDetailData = z.infer<typeof apiResponseDataSchema>;

export const queryFn = async (userId: string, signal?: AbortSignal) => {
  const { data: resp } = await basicApi.get<APIResponse>(
    API_PATH.replace(PATH_VAR, userId),
    { signal },
  );

  return apiResponseDataSchema.parse(resp.data);
};

export const useQueryUserDetail = (
  userId?: string,
  options?: QueryOptions<QueryUserDetailData>,
) => {
  return useQuery({
    queryKey: [QUERY_USER_DETAIL_KEY, userId],
    queryFn: userId ? ({ signal }) => queryFn(userId, signal) : skipToken,
    ...options,
  });
};
